import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Ruler, Mountains, ShieldCheck, Building, GridFour, MapPinLine, CheckCircle } from "@phosphor-icons/react";

// Curated list of the 4 most-delivered service mixes per region tier.
// Kept here so the panel is self-contained; swap for a real API later.
const TIER_SERVICES = {
    flagship: [
        { icon: Ruler,       name: "Boundary Survey",        share: 32 },
        { icon: Mountains,   name: "Topographic Survey",     share: 28 },
        { icon: ShieldCheck, name: "ALTA/NSPS Survey",       share: 22 },
        { icon: Building,    name: "Site Plan",              share: 18 },
    ],
    top: [
        { icon: GridFour,    name: "Subdivision Plat",       share: 30 },
        { icon: Ruler,       name: "Boundary Survey",        share: 28 },
        { icon: Mountains,   name: "Topographic Survey",     share: 24 },
        { icon: MapPinLine,  name: "Construction Staking",   share: 18 },
    ],
    strong: [
        { icon: Mountains,   name: "Topographic Survey",     share: 33 },
        { icon: Ruler,       name: "Boundary Survey",        share: 30 },
        { icon: Building,    name: "Site Plan",              share: 22 },
        { icon: MapPinLine,  name: "As-Built Survey",        share: 15 },
    ],
    active: [
        { icon: Ruler,       name: "Boundary Survey",        share: 40 },
        { icon: Mountains,   name: "Topographic Survey",     share: 32 },
        { icon: Building,    name: "Site Plan",              share: 28 },
    ],
    growing: [
        { icon: Ruler,       name: "Boundary Survey",        share: 55 },
        { icon: Mountains,   name: "Topographic Survey",     share: 45 },
    ],
};

function tierKey(count) {
    if (count >= 6000) return "flagship";
    if (count >= 3000) return "top";
    if (count >= 1000) return "strong";
    if (count >= 500) return "active";
    return "growing";
}

function tierMeta(count) {
    const key = tierKey(count);
    return {
        flagship: { label: "Flagship region",  color: "#22d3ee", glow: "rgba(34,211,238,0.5)" },
        top:      { label: "Top tier",          color: "#06b6d4", glow: "rgba(6,182,212,0.4)" },
        strong:   { label: "Strong secondary",  color: "#0891b2", glow: "rgba(8,145,178,0.35)" },
        active:   { label: "Active region",     color: "#64748b", glow: "rgba(100,116,139,0.3)" },
        growing:  { label: "Growing",           color: "#475569", glow: "rgba(71,85,105,0.25)" },
    }[key];
}

export default function StateDetailPanel({ open, state, onClose }) {
    // ESC to close.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!state) return null;
    const meta = tierMeta(state.count);
    const services = TIER_SERVICES[tierKey(state.count)] || TIER_SERVICES.growing;

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
                        data-testid="state-panel-backdrop"
                    />
                    <motion.aside
                        key="panel"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 28, stiffness: 260 }}
                        className="fixed top-0 right-0 z-[61] h-full w-full sm:w-[440px] lg:w-[480px] bg-[#050912] border-l border-slate-800 shadow-2xl flex flex-col"
                        data-testid="state-panel"
                        role="dialog"
                        aria-label={`${state.name} delivery detail`}
                    >
                        {/* Gradient header */}
                        <div
                            className="px-6 pt-6 pb-5 border-b border-slate-800 relative overflow-hidden"
                            style={{
                                background: `linear-gradient(135deg, rgba(3,7,18,0.9), rgba(11,17,32,0.9)),
                                    radial-gradient(circle at 100% 0%, ${meta.glow}, transparent 65%)`,
                            }}
                        >
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-colors"
                                aria-label="Close"
                                data-testid="state-panel-close"
                            >
                                <X size={18} weight="bold" />
                            </button>
                            <div
                                className="inline-flex items-center gap-2 text-[10px] font-mono-alt uppercase tracking-[1.5px] px-2.5 py-1 rounded-full border"
                                style={{ color: meta.color, borderColor: `${meta.color}66`, background: `${meta.color}15` }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}` }} />
                                {meta.label}
                            </div>
                            <h2 className="font-heading text-3xl font-bold text-slate-50 tracking-tight mt-3">
                                {state.name}
                            </h2>
                            <div className="flex items-baseline gap-3 mt-2">
                                <span
                                    className="font-heading text-5xl font-extrabold font-mono-alt leading-none"
                                    style={{ color: meta.color }}
                                >
                                    {state.count ? `${state.count.toLocaleString()}+` : "0"}
                                </span>
                                <span className="text-xs text-slate-400 font-medium">drawings delivered</span>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                            {/* Most-requested services in this tier */}
                            <section>
                                <div className="overline mb-3">Most-requested services here</div>
                                <ul className="space-y-2" data-testid="state-panel-services">
                                    {services.map((s) => (
                                        <li
                                            key={s.name}
                                            className="flex items-center gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40"
                                        >
                                            <div
                                                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                                style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}33` }}
                                            >
                                                <s.icon size={17} weight="duotone" style={{ color: meta.color }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-heading text-sm font-semibold text-slate-100 truncate">
                                                    {s.name}
                                                </div>
                                                <div className="h-1 bg-slate-800 rounded-full mt-1.5 overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{ width: `${s.share}%`, background: meta.color }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-xs font-mono-alt text-slate-400 flex-shrink-0">
                                                {s.share}%
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            {/* Why firms in this region pick us */}
                            <section>
                                <div className="overline mb-3">Why firms here pick Surveys2CAD</div>
                                <ul className="space-y-2 text-sm text-slate-300">
                                    {[
                                        "Drafters familiar with local coordinate system & recording standards",
                                        "FEMA flood map panels + state-plane conversions at no extra cost",
                                        "24-hour rush delivery available for most service types",
                                        "Fixed-price quotes — no surprises on invoice day",
                                    ].map((b) => (
                                        <li key={b} className="flex items-start gap-2.5">
                                            <CheckCircle size={16} weight="fill" style={{ color: meta.color }} className="mt-0.5 flex-shrink-0" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        </div>

                        {/* Sticky footer CTA */}
                        <div className="border-t border-slate-800 bg-[#050912] p-4 space-y-2">
                            <Link
                                to={`/get-started?state=${encodeURIComponent(state.name)}`}
                                onClick={onClose}
                                className="btn-primary w-full justify-center"
                                data-testid="state-panel-cta"
                            >
                                Get a {state.name} quote <ArrowRight size={15} weight="bold" />
                            </Link>
                            <Link
                                to="/services"
                                onClick={onClose}
                                className="btn-ghost w-full justify-center !py-2.5 text-xs"
                                data-testid="state-panel-services-link"
                            >
                                Browse all 12 services
                            </Link>
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
